import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { View, StyleSheet, Text, TextInput, Pressable, ActivityIndicator, ScrollView } from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { faCircleLocationArrow } from "@fortawesome/pro-solid-svg-icons";
import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import { faMagnifyingGlass, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import {
  FontAwesomeIcon,
  homeIconSizes,
  screenWidth,
  os,
  platform,
  logAnalyticsEvent,
  logError,
  isMobileOrMobileWeb,
} from "../../platform";
import AppStore from "../../storage/appStore";
import SEOHeaderTags from "../SEOHeaderTags";
import { COLORS } from "../../theme/colors";
import { STRINGS } from "../../constants/strings";
import { debounce } from "../../utils/debounce";
import {
  getAddress,
  getPropertyInfo,
  getAddressFromCoordinates,
  getPropertyInfoMapBoundaries,
} from "../../utils/api/propertyApi";
import HoneypotInput from "../../sub/components/HoneypotInput";
import { useFunctionDisable } from "../../utils/functionDisable";
import DropdownPortal from "./DropdownPortal";

const isMobileWebCheck = isMobileOrMobileWeb();

const AddressLookup = ({ suggestedAddresses, setSuggestedAddresses, hideKeyboardAndDropdown }) => {
  const addressAPILoading = AppStore((state) => state.addressAPILoading);
  const setAddressAPILoading = AppStore((state) => state.setAddressAPILoading);
  const setErrorMessage = AppStore((state) => state.setErrorMessage);
  const setLoading = AppStore((state) => state.setLoading);
  const setPropertyInfo = AppStore((state) => state.setPropertyInfo);
  const setGarbageInfo = AppStore((state) => state.setGarbageInfo);
  const setPropertyInfoMapBoundaries = AppStore((state) => state.setPropertyInfoMapBoundaries);
  const submitEnabled = useFunctionDisable(3000);
  const [address, setAddress] = useState("");
  const [enableAddressSearch, setEnableAddressSearch] = useState(false);
  const [getLocationButtonEnabled, setGetLocationButtonEnabled] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const isFocused = useIsFocused();

  const abortControllerRef = useRef(null);
  const textInputRef = useRef(null);
  const containerRef = useRef(null);
  const scrollViewRef = useRef(null);
  const dropdownItemRefs = useRef([]);

  const locationRequestRef = useRef(null);

  useEffect(() => {
    if (!isFocused) {
      setSuggestedAddresses([]);
      setSelectedIndex(-1);
    }
  }, [isFocused, setSuggestedAddresses]);

  // Measure TextInput position whenever suggestions appear
  const measureTextInput = useCallback(() => {
    if (platform === "web") {
      const node = textInputRef.current?._node || textInputRef.current;
      if (node?.getBoundingClientRect) {
        const rect = node.getBoundingClientRect();
        setDropdownPosition({ top: rect.bottom, left: rect.left, width: rect.width });
      }
    } else {
      // Native: measure relative to the container (nearest positioned ancestor)
      textInputRef.current?.measureLayout(
        containerRef.current,
        (x, y, width, height) => {
          setDropdownPosition({ top: y + height, left: x, width });
        },
        () => {
          logError("dropdown_measure_layout_failed", {});
        }
      );
    }
  }, []);

  useEffect(() => {
    if (suggestedAddresses.length === 0) {
      dropdownItemRefs.current = [];
    } else {
      measureTextInput();
    }
  }, [suggestedAddresses, measureTextInput]);

  // Scroll to selected item when selectedIndex changes
  useEffect(() => {
    if (selectedIndex >= 0 && dropdownItemRefs.current[selectedIndex] && scrollViewRef.current) {
      dropdownItemRefs.current[selectedIndex].measureLayout(
        scrollViewRef.current,
        (_x, y) => {
          scrollViewRef.current.scrollTo({
            y: y,
            animated: true,
          });
        },
        () => {
          logError("dropdown_measurement_failed", {
            selectedIndex,
            hasRef: !!dropdownItemRefs.current[selectedIndex],
          });
        }
      );
    }
  }, [selectedIndex]);

  useEffect(() => {
    return () => {
      if (locationRequestRef.current) {
        locationRequestRef.current.cancel();
      }
    };
  }, []);

  const loadingAddress = useCallback(
    (loading) => {
      setAddressAPILoading(loading);
      setGetLocationButtonEnabled(!loading);
    },
    [setAddressAPILoading]
  );

  const debouncedFetchAddresses = useMemo(
    () =>
      debounce(async (text) => {
        // Cancel any previous request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        try {
          loadingAddress(true);
          const addresses = await getAddress(text);

          if (addresses.length > 0) {
            setSuggestedAddresses(addresses);
          } else {
            setSuggestedAddresses([STRINGS.noResultsFound]);
          }
        } catch (error) {
          logError("address_search_failed", {
            message: error.message,
            stack: error.stack,
            searchText: text,
          });
          // Don't update state if request was aborted
          if (error.name !== "AbortError") {
            setSuggestedAddresses([STRINGS.noResultsFound]);
          }
        } finally {
          loadingAddress(false);
        }
      }, 500),
    [loadingAddress, setSuggestedAddresses]
  );

  useEffect(() => {
    return () => {
      if (debouncedFetchAddresses.cancel) debouncedFetchAddresses.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [debouncedFetchAddresses]);

  /**
   * Handles changes to the address input field.
   * Updates the address state, manages suggested addresses based on input length,
   * and triggers address search when appropriate.
   *
   * @param {string} text - The current value of the address input field.
   * @returns {void}
   */
  const handleAddressChange = useCallback(
    (text) => {
      if (locationRequestRef.current) {
        locationRequestRef.current.cancel();
        locationRequestRef.current = null;
        loadingAddress(false);
      }

      setAddress(text);
      setSelectedIndex(-1);
      const trimmedText = text.trim();
      if (trimmedText === "" || trimmedText.length < 5) {
        if (debouncedFetchAddresses.cancel) debouncedFetchAddresses.cancel();
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        setSuggestedAddresses([]);
        setEnableAddressSearch(false);
        return;
      }

      debouncedFetchAddresses(text);
    },
    [debouncedFetchAddresses, setSuggestedAddresses, loadingAddress]
  );

  /**
   * Handles the event when the user presses the "Get Location" button.
   * Initiates an API call to fetch the address based on coordinates.
   * Sets loading state while fetching, updates address and error states based on the response.
   * Enables address search if the address is successfully retrieved.
   * Handles errors by displaying an appropriate error message.
   *
   * @async
   * @function handleGetLocationPress
   * @returns {Promise<void>} Resolves when the address fetching process is complete.
   */
  const handleGetLocationPress = useCallback(async () => {
    try {
      loadingAddress(true);

      if (locationRequestRef.current) {
        locationRequestRef.current.cancel();
      }

      const request = getAddressFromCoordinates();
      locationRequestRef.current = request;

      const result = await request.promise;

      if (!result) return;

      if (!result.success) {
        setErrorMessage(STRINGS.errorFetchingAddress);
        return;
      }

      if (result.data?.address) {
        setAddress(result.data.address);
        setEnableAddressSearch(true);
      }
    } catch (error) {
      logError("geolocation_address_failed", {
        message: error.message,
        stack: error.stack,
      });
      setErrorMessage(STRINGS.errorFetchingAddress);
    } finally {
      locationRequestRef.current = null;
      loadingAddress(false);
    }
  }, [loadingAddress, setErrorMessage]);

  const handleFindMySchedulePress = useCallback(async () => {
    if (!submitEnabled) return;

    setLoading(true);
    let response = {};

    try {
      response = await getPropertyInfo(address);
      setPropertyInfo(response?.propertyInfo);
      setGarbageInfo(response.propertyInfo?.garbage);
    } catch (error) {
      logError("property_info_fetch_failed", {
        message: error.message,
        stack: error.stack,
        address,
      });
      setErrorMessage(STRINGS.addressNotFound);
      setLoading(false);
      return;
    }

    if (platform === "native") {
      try {
        const boundaries = await getPropertyInfoMapBoundaries(response?.propertyInfo?.address);
        setPropertyInfoMapBoundaries(boundaries.mapBoundaries);
      } catch (error) {
        logError("map_boundaries_fetch_failed", {
          message: error.message,
          stack: error.stack,
          address,
        });
      }
    }

    setLoading(false);
  }, [
    address,
    submitEnabled,
    setLoading,
    setPropertyInfo,
    setErrorMessage,
    setPropertyInfoMapBoundaries,
    setGarbageInfo,
  ]);

  // Memoize clear address handler
  const handleClearAddress = useCallback(() => {
    setAddress("");
    setSuggestedAddresses([]);
    setEnableAddressSearch(false);
    setSelectedIndex(-1);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, [setSuggestedAddresses]);

  // Memoize address selection handler
  const handleAddressSelect = useCallback(
    (selectedAddress) => {
      setAddress(selectedAddress);
      setEnableAddressSearch(true);
      setSelectedIndex(-1);
      hideKeyboardAndDropdown();

      // Focus on TextInput after selection (web only)
      if (os === "web" && !isMobileWebCheck && textInputRef.current) {
        textInputRef.current.focus();
      }
    },
    [hideKeyboardAndDropdown]
  );

  // Memoize key press handler
  const handleKeyPress = useCallback(
    (e) => {
      // Keyboard navigation only supported on web
      if (os !== "web") return;

      const key = e.nativeEvent.key;

      // Filter out "no results found" from navigation
      const validAddresses = suggestedAddresses.filter((addr) => addr !== STRINGS.noResultsFound);

      if (key === "ArrowDown") {
        e.preventDefault();
        if (validAddresses.length > 0) {
          setSelectedIndex((prev) => (prev < validAddresses.length - 1 ? prev + 1 : prev));
        }
      } else if (key === "ArrowUp") {
        e.preventDefault();
        if (validAddresses.length > 0) {
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        }
      } else if (key === "Enter") {
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < validAddresses.length) {
          handleAddressSelect(validAddresses[selectedIndex]);
        } else if (enableAddressSearch) {
          handleFindMySchedulePress();
        }
      } else if (key === "Escape") {
        setSuggestedAddresses([]);
        setSelectedIndex(-1);
      }
    },
    [
      suggestedAddresses,
      selectedIndex,
      enableAddressSearch,
      handleFindMySchedulePress,
      handleAddressSelect,
      setSuggestedAddresses,
    ]
  );

  // Memoize dropdown item renderer to prevent inline function creation
  const renderDropdownItem = useCallback(
    (item, index) => {
      const handlePress = () => handleAddressSelect(item);
      const isSelected = index === selectedIndex;
      return (
        <Pressable
          key={item}
          ref={(el) => (dropdownItemRefs.current[index] = el)}
          style={[styles.dropdownItem, isSelected && styles.dropdownItemSelected]}
          onPress={handlePress}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel={item}
          accessibilityState={{ selected: isSelected }}
          accessibilityHint={isSelected ? "Currently selected. Press Enter to choose this address" : undefined}
        >
          <Text style={[styles.dropdownText, isSelected && styles.dropdownTextSelected]} maxFontSizeMultiplier={1.2}>
            {item}
          </Text>
        </Pressable>
      );
    },
    [handleAddressSelect, selectedIndex]
  );

  // Memoize text input style
  const textInputStyle = useMemo(
    () => [
      styles.addressTextInput,
      suggestedAddresses?.length > 0 ? styles.addressTextInputWithDropdown : styles.addressTextInputWithoutDropdown,
    ],
    [suggestedAddresses?.length]
  );

  // Memoize button style
  const buttonStyle = useMemo(
    () => [styles.addressInputButton, !enableAddressSearch && styles.addressInputButtonDisabled],
    [enableAddressSearch]
  );

  return (
    <View ref={containerRef} style={styles.container} testID="address-lookup-component">
      <View style={styles.garbageNotificationsContainer}>
        <View style={styles.garbageIconTextContainer}>
          <View style={styles.garbageIconContainer}>
            <FontAwesomeIcon icon={faTrashCan} size={homeIconSizes.garbage} color={COLORS.quaternary} />
          </View>
          <SEOHeaderTags
            style={styles.garbageNotificationsText}
            text={STRINGS.garbageRecyclingNotifications}
            tag="h2"
          />
        </View>
      </View>

      <View style={styles.addressInputContainer}>
        <Text style={styles.addressInputLabel} accessible={true} accessibilityRole="text" maxFontSizeMultiplier={1.2}>
          {STRINGS.yourAddress}
        </Text>
        <View style={styles.inputWrapper}>
          <TextInput
            ref={textInputRef}
            style={textInputStyle}
            maxFontSizeMultiplier={1.2}
            placeholder={STRINGS.streetAddress}
            placeholderTextColor={COLORS.tertiary}
            keyboardType="default"
            value={address}
            onChangeText={handleAddressChange}
            onKeyPress={handleKeyPress}
            onLayout={suggestedAddresses.length > 0 ? measureTextInput : undefined}
            accessible={true}
            accessibilityHint="Enter your street address to find your garbage collection schedule. Use arrow keys to navigate suggestions"
            accessibilityRole="combobox"
            aria-autocomplete="list"
            aria-expanded={suggestedAddresses?.length > 0}
            aria-controls="address-dropdown"
          />

          <HoneypotInput name="address_lookup" analytics={logAnalyticsEvent} />

          <Pressable
            testID="get-location-button"
            style={styles.getLocationButton}
            onPress={handleGetLocationPress}
            disabled={!getLocationButtonEnabled}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel={STRINGS.getAddress}
          >
            <FontAwesomeIcon icon={faCircleLocationArrow} size={homeIconSizes.addressLookupIcons} color={COLORS.text} />
          </Pressable>
          {addressAPILoading && (
            <ActivityIndicator
              style={styles.addressInputIcons}
              color={COLORS.text}
              accessibilityLabel="Loading address"
            />
          )}
          {!addressAPILoading && address.length > 0 && (
            <Pressable
              testID="clear-address-button"
              style={styles.addressInputIcons}
              onPress={handleClearAddress}
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel={STRINGS.clearAddress}
            >
              <FontAwesomeIcon icon={faCircleXmark} size={homeIconSizes.addressLookupIcons} color={COLORS.text} />
            </Pressable>
          )}
        </View>
        {os === "android" && suggestedAddresses?.length > 0 && suggestedAddresses[0] !== STRINGS.noResultsFound && (
          <ScrollView
            ref={scrollViewRef}
            style={styles.dropdownAndroid}
            keyboardShouldPersistTaps="handled"
            nestedScrollEnabled={true}
            accessible={true}
            accessibilityRole="list"
            accessibilityLabel="Address suggestions"
            accessibilityLiveRegion="polite"
            nativeID="address-dropdown"
          >
            {suggestedAddresses.map((item, index) => renderDropdownItem(item, index))}
          </ScrollView>
        )}
        {!(os === "android" && suggestedAddresses?.length > 0 && suggestedAddresses[0] !== STRINGS.noResultsFound) && (
          <Pressable
            testID="find-schedule-button"
            style={buttonStyle}
            disabled={!enableAddressSearch}
            onPress={handleFindMySchedulePress}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel={STRINGS.findMySchedule}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} size={homeIconSizes.button} color={COLORS.onPrimary} />
            <Text style={styles.addressInputButtonText} maxFontSizeMultiplier={1.2}>
              {STRINGS.findMySchedule}
            </Text>
          </Pressable>
        )}
      </View>

      {os !== "android" && suggestedAddresses?.length > 0 && suggestedAddresses[0] !== STRINGS.noResultsFound && (
        <DropdownPortal>
          <ScrollView
            ref={scrollViewRef}
            style={[
              styles.dropdown,
              { top: dropdownPosition.top, left: dropdownPosition.left, width: dropdownPosition.width },
            ]}
            keyboardShouldPersistTaps="handled"
            nestedScrollEnabled={true}
            accessible={true}
            accessibilityRole="list"
            accessibilityLabel="Address suggestions"
            accessibilityLiveRegion="polite"
            nativeID="address-dropdown"
          >
            {suggestedAddresses.map((item, index) => renderDropdownItem(item, index))}
          </ScrollView>
        </DropdownPortal>
      )}

      {suggestedAddresses?.[0] === STRINGS.noResultsFound && (
        <DropdownPortal>
          <View
            style={[
              styles.dropdown,
              { top: dropdownPosition.top, left: dropdownPosition.left, width: dropdownPosition.width },
            ]}
            accessibilityLiveRegion="polite"
          >
            <View style={styles.dropdownItem}>
              <Text style={styles.dropdownText} accessible={true} accessibilityRole="text" maxFontSizeMultiplier={1.2}>
                {STRINGS.noResultsFound}
              </Text>
            </View>
          </View>
        </DropdownPortal>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    zIndex: 1000,
  },
  garbageNotificationsContainer: {
    alignItems: "center",
    paddingHorizontal: screenWidth > 520 ? 0 : screenWidth * 0.05,
  },
  garbageIconTextContainer: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "center",
  },
  garbageIconContainer: {
    backgroundColor: COLORS.tertiaryVariant,
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 8,
  },
  garbageNotificationsText: {
    fontSize: screenWidth > 400 ? 28 : 23,
    paddingLeft: 10,
    flexShrink: 1,
    alignSelf: "center",
    color: COLORS.quaternary,
  },
  addressInputContainer: {
    flexDirection: "column",
    gap: 10,
    marginTop: 10,
    backgroundColor: COLORS.tertiaryVariant,
    paddingVertical: 10,
    paddingHorizontal: 25,
    borderRadius: 8,
  },
  addressInputLabel: {
    fontSize: 14,
    paddingVertical: 1,
    color: COLORS.uiGray700,
    fontWeight: "bold",
  },
  inputWrapper: {
    position: "relative",
  },
  addressTextInput: {
    fontSize: 16,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: 15,
    paddingLeft: 45,
    paddingRight: 45,
    backgroundColor: COLORS.surface,
    color: COLORS.uiGray700,
    outlineStyle: "none",
  },
  addressTextInputWithoutDropdown: {
    borderRadius: 8,
  },
  addressTextInputWithDropdown: {
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  addressInputIcons: {
    position: "absolute",
    right: 15,
    top: 15,
  },
  getLocationButton: {
    position: "absolute",
    left: 15,
    top: 15,
  },
  addressInputButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: 15,
    borderRadius: 25,
    backgroundColor: COLORS.red,
  },
  addressInputButtonDisabled: {
    backgroundColor: COLORS.tertiary,
  },
  dropdown: {
    position: platform === "web" ? "fixed" : "absolute",
    maxHeight: 147,
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: COLORS.surface,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    zIndex: 9999,
    borderTopWidth: 0,
  },
  dropdownAndroid: {
    maxHeight: 147,
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: COLORS.surface,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    borderTopWidth: 0,
    marginTop: -10,
  },
  dropdownItem: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
  },
  dropdownItemSelected: {
    backgroundColor: COLORS.primary,
  },
  dropdownText: {
    fontSize: 16,
    color: COLORS.uiGray700,
  },
  dropdownTextSelected: {
    color: COLORS.onPrimary,
  },
  addressInputButtonText: {
    fontSize: 16,
    fontWeight: "bold",
    color: COLORS.onPrimary,
  },
});

AddressLookup.displayName = "AddressLookup";

export default AddressLookup;
